class MainProduct extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.form = this.querySelector('form');
    this.configurator = this.querySelector('.product-configurator');
    this.popup = this.querySelector('.product-upsell__popup');
    this.buyButton = this.querySelector('button.buy-now');
    this.initSlider();

    if (this.configurator) {
      this.variants = JSON.parse(this.configurator.querySelector('script[type="application/json"]').textContent);
      const content = this.querySelector('.product__content-container');
      content.addEventListener('change', event => this.onChange(event), { signal });
      this.update();
    }

    if (this.popup) {
      this.addEventListener('click', event => this.onPopupClick(event), { signal });
      window.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.popup.classList.contains('open')) this.togglePopup(false);
      }, { signal });
    }

    if (this.buyButton) {
      this.buyButton.addEventListener('click', event => this.onBuy(event), { signal });
    }

    this.addEventListener('click', event => this.onToggleClick(event), { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  initSlider() {
    this.swiper = new Swiper(this.querySelector('.swiper-product'), {
      slidesPerView: 1,
      centeredSlides: true,
      loop: true,
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true
      },
      navigation: {
        nextEl: this.querySelector('.swiper--next'),
        prevEl: this.querySelector('.swiper--prev')
      }
    });

    this.swiperT = new Swiper(this.querySelector('.swiper-text'), {
      slidesPerView: 'auto',
      loop: true,
      centeredSlides: true,
      autoplay: { delay: 5000, disableOnInteraction: false },
      pagination: {
        el: this.querySelector('.swiper-pagination-text'),
        clickable: false
      }
    });
  }

  onChange(event) {
    const input = event.target;
    if (input.name === 'guide') {
      this.showGuide(input.value);
      return;
    }
    if (input.name === 'upsell') this.selectUpsell(input);
    if (input.name === 'plan') this.form.elements.purchase.value = 'subscription';
    this.update();
  }

  selectUpsell(checkbox) {
    this.form.querySelectorAll('input[name="upsell"]').forEach(input => {
      if (input !== checkbox) input.checked = false;
    });
  }

  showGuide(index) {
    this.querySelectorAll('[data-guide]').forEach(table => {
      table.hidden = table.dataset.guide !== index;
    });
  }

  onPopupClick(event) {
    if (event.target.closest('[data-upsell-open]')) this.togglePopup(true);
    if (event.target.closest('[data-upsell-close]') || event.target === this.popup) this.togglePopup(false);
  }

  togglePopup(open) {
    this.popup.classList.toggle('open', open);
    document.body.classList.toggle('overflow-hidden', open);
  }

  onToggleClick(event) {
    const item = event.target.closest('toggle-item');
    if (!item || !event.target.closest('h3')) return;
    this.querySelectorAll('toggle-item.actif').forEach(other => {
      if (other !== item) {
        other.classList.remove('actif');
        other.querySelector('.toggle-item__content').style.height = '0px';
      }
    });
  }

  findVariant(flavour, size) {
    return this.variants.find(variant => variant.option1 === flavour && variant.option2 === size);
  }

  formatPrice(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + '€';
  }

  formatUnitPrice(cents, variant) {
    return `${this.formatPrice(Math.round(cents / variant.option2))} ${this.configurator.dataset.unitSuffix}`;
  }

  setText(selector, text) {
    this.configurator.querySelectorAll(selector).forEach(element => (element.textContent = text));
  }

  update() {
    const fields = this.form.elements;
    const mode = fields.mode ? fields.mode.value : 'single';
    const flavour = mode === 'variety' ? this.configurator.dataset.variety : fields.flavour.value;
    const variant = this.findVariant(flavour, fields.size.value) || this.variants.find(item => item.option1 === flavour);
    const plan = variant.selling_plan_allocations[fields.plan ? fields.plan.value : 0];
    fields.size.value = variant.option2;
    if (!plan) fields.purchase.value = 'onetime';
    const isSubscription = fields.purchase.value === 'subscription';

    this.configurator.querySelectorAll('[data-mode-content]').forEach(element => {
      element.hidden = element.dataset.modeContent !== mode;
    });
    this.configurator.querySelectorAll('[data-shipping-note]').forEach(element => {
      element.hidden = element.dataset.shippingNote !== variant.option2;
    });
    this.configurator.querySelectorAll('input[name="size"]').forEach(input => {
      input.disabled = !this.findVariant(flavour, input.value);
    });
    this.configurator.querySelectorAll('input[name="flavour"]').forEach(input => {
      input.closest('label').toggleAttribute('data-sold-out', !this.findVariant(input.value, variant.option2)?.available);
    });

    this.setText('[data-count]', variant.option2);
    this.setText('[data-variety-count]', variant.option2 / this.configurator.querySelectorAll('.product-configurator__pack-card').length);
    this.setText('[data-onetime-price]', this.formatPrice(variant.price));
    this.setText('[data-onetime-unit]', this.formatUnitPrice(variant.price, variant));
    if (plan) {
      this.setText('[data-saving]', Math.round((1 - plan.price / variant.price) * 100));
      this.setText('[data-subscription-compare]', this.formatPrice(variant.price));
      this.setText('[data-subscription-price]', this.formatPrice(plan.price));
      this.setText('[data-subscription-unit]', this.formatUnitPrice(plan.price, variant));
    }

    fields.id.value = variant.id;
    fields.selling_plan.value = isSubscription ? plan.selling_plan_id : '';
    this.updateBuyButton(variant.available, isSubscription ? plan.price : variant.price);
  }

  updateBuyButton(available, price) {
    if (!this.buyButton) return;
    const upsell = this.form.querySelector('input[name="upsell"]:checked');
    const total = upsell ? price + Number(upsell.dataset.price) : price;
    this.buyButton.disabled = !available;
    this.buyButton.querySelector('[data-buy-label]').textContent = available
      ? this.buyButton.dataset.label
      : this.buyButton.dataset.soldOutLabel;
    this.buyButton.querySelector('.sticky-price').textContent = this.formatPrice(total);
  }

  async onBuy(event) {
    event.preventDefault();
    const fields = this.form.elements;
    const item = { id: fields.id.value, quantity: 1 };
    if (fields.selling_plan.value) item.selling_plan = fields.selling_plan.value;
    const upsell = this.form.querySelector('input[name="upsell"]:checked');

    this.buyButton.disabled = true;
    await fetch('/cart/clear.js', { method: 'POST', signal: this.controller.signal });
    const response = await this.addToCart(item);
    if (response.ok && upsell) await this.addToCart({ id: upsell.value, quantity: 1 });

    if (response.ok) {
      window.location.href = '/checkout';
    } else {
      this.buyButton.disabled = false;
      alert('Une erreur est survenue');
    }
  }

  addToCart(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] }),
      signal: this.controller.signal
    });
  }
}

customElements.define('main-product', MainProduct);
